import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { environment } from '@customer-portal/environments';

import { ScopedTranslocoLoaderService } from './scoped-transloco-loader.service';

export const createClearTranslationsCache =
  (cache: Map<string, boolean>) => () =>
    cache.clear();

export const createScopedTranslationsResolver =
  (
    scope: string,
    cache: Map<string, boolean>,
    fallbackImport: (lang: string) => Promise<any>,
  ): ResolveFn<{ showError: boolean; isBlobError: boolean }> =>
  async () => {
    const translocoService = inject(TranslocoService);
    const loaderService = inject(ScopedTranslocoLoaderService);

    const currentLang = translocoService.getActiveLang();
    const cacheKey = `${scope}-${currentLang}`;

    if (cache.has(cacheKey)) return { showError: false, isBlobError: false };

    const setTranslation = (translation: any, lang: string) => {
      translocoService.setTranslation({ [scope]: translation }, lang, {
        merge: true,
      });
      cache.set(`${scope}-${lang}`, true);
    };

    async function loadAndSetTranslation(lang: string) {
      try {
        const translation = await loaderService.loadTranslation(scope, lang);
        setTranslation(translation, lang);

        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to load ${lang} from blob for ${scope}:`, error);

        return false;
      }
    }

    if (currentLang !== 'en' && !cache.has(`${scope}-en`)) {
      await loadAndSetTranslation('en');
    }

    type TranslationStrategy = {
      name: string;
      lang: string;
      loader: () => Promise<any>;
    };

    const strategies: TranslationStrategy[] = environment.translation
      .useLocalTranslations
      ? [
          {
            name: 'local asset',
            lang: currentLang,
            loader: async () =>
              (await fallbackImport(currentLang)).default ||
              (await fallbackImport(currentLang)),
          },
        ]
      : [
          {
            name: 'current language from blob',
            lang: currentLang,
            loader: () => loaderService.loadTranslation(scope, currentLang),
          },
          ...(currentLang !== 'en'
            ? [
                {
                  name: 'English from blob',
                  lang: currentLang,
                  loader: () => loaderService.loadTranslation(scope, 'en'),
                },
              ]
            : []),
          {
            name: 'local English asset',
            lang: currentLang,
            loader: async () =>
              (await fallbackImport('en')).default ||
              (await fallbackImport('en')),
          },
        ];

    let blobError = false;
    let translationLoaded = false;

    async function tryStrategies(strategiesToParse: TranslationStrategy[]) {
      const tryNext = async (index: number): Promise<void> => {
        if (index >= strategiesToParse.length) return;
        const { name, lang, loader } = strategiesToParse[index];

        try {
          const translation = await loader();
          setTranslation(translation, lang);
          translationLoaded = true;
        } catch (error) {
          if (name === 'current language from blob') blobError = true;
          // eslint-disable-next-line no-console
          console.warn(`Failed to load ${scope} translation (${name}):`, error);
          await tryNext(index + 1);
        }
      };
      await tryNext(0);
    }

    await tryStrategies(strategies);

    if (!translationLoaded) {
      // eslint-disable-next-line no-console
      console.error(
        `All translation loading strategies failed for ${scope}. Translations will be missing.`,
      );

      return { showError: true, isBlobError: blobError };
    }

    return { showError: blobError, isBlobError: blobError };
  };
